import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  Person as PersonIcon
} from '@mui/icons-material';

function TaskCard({ task, onEdit, onDelete, onStatusChange, onClick }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onEdit(task);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onDelete(task);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(task);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'primary',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      not_started: 'default',
      in_progress: 'info',
      completed: 'success',
      blocked: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      not_started: 'À faire',
      in_progress: 'En cours',
      completed: 'Terminé',
      blocked: 'Bloqué'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      low: 'Basse',
      medium: 'Moyenne',
      high: 'Haute',
      urgent: 'Urgente'
    };
    return labels[priority] || priority;
  };

  const isOverdue = task.end_date && new Date(task.end_date) < new Date() && task.status !== 'completed';

  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          boxShadow: 4,
          transform: 'translateY(-2px)'
        } : {},
        ...(isOverdue && {
          borderLeft: '4px solid',
          borderColor: 'error.main'
        })
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: '1rem' }}>
            {task.title}
          </Typography>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEdit}>Modifier</MenuItem>
            {onStatusChange && task.status !== 'completed' && (
              <MenuItem onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                onStatusChange(task, 'completed');
              }}>
                Marquer comme terminé
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              Supprimer
            </MenuItem>
          </Menu>
        </Box>

        {task.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {task.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={getStatusLabel(task.status)}
            size="small"
            color={getStatusColor(task.status)}
          />
          <Chip
            label={getPriorityLabel(task.priority)}
            size="small"
            color={getPriorityColor(task.priority)}
            variant="outlined"
          />
          {task.phase && (
            <Chip
              label={task.phase}
              size="small"
              variant="outlined"
            />
          )}
          {task.tags && task.tags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              sx={{
                bgcolor: tag.color,
                color: 'white'
              }}
            />
          ))}
        </Box>

        {task.progress !== undefined && task.progress !== null && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Progression
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {task.progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={task.progress}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {task.assignees && task.assignees.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {task.assignees.slice(0, 3).map((assignee, index) => (
                  <Tooltip key={assignee.id} title={assignee.full_name || assignee.name}>
                    <Avatar
                      src={assignee.avatar_url}
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        fontSize: '0.75rem',
                        ml: index > 0 ? -1 : 0,
                        border: '2px solid white',
                        bgcolor: assignee.avatar_color || 'primary.main'
                      }}
                    >
                      {(assignee.full_name || assignee.name || '?').charAt(0)}
                    </Avatar>
                  </Tooltip>
                ))}
                {task.assignees.length > 3 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    +{task.assignees.length - 3}
                  </Typography>
                )}
              </Box>
            ) : task.assignee_name ? (
              <Tooltip title={task.assignee_name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {task.assignee_avatar ? (
                    <Avatar
                      src={task.assignee_avatar}
                      alt={task.assignee_name}
                      sx={{ width: 24, height: 24 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.875rem' }}>
                      {task.assignee_name.charAt(0)}
                    </Avatar>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {task.assignee_name.split(' ')[0]}
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}

            {task.end_date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography
                  variant="caption"
                  color={isOverdue ? 'error' : 'text.secondary'}
                  sx={{ fontWeight: isOverdue ? 'bold' : 'normal' }}
                >
                  {new Date(task.end_date).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
            )}

            {task.estimated_hours && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimerIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {task.estimated_hours}h
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TaskCard;
